import { useState } from 'react';

import PropTypes from 'prop-types';

import SampleRecipientField from '#contactListFeature/components/SampleRecipientField/SampleRecipientField';
import { sampleRecipientRef } from '#contactListFeature/components/SampleRecipientField/sampleRecipientRef';
import Button from '#globalComponents/Button/Button';
import FormField from '#globalComponents/Forms/FormField';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useAuth } from '#globalContexts/AuthProvider';
import { useError } from '#globalContexts/ErrorContext';
import { useAllEmailAccounts } from '#settingsFeature/models/emailAccounts/queries';

import { sendTestEmail } from '../../../api/testEmailApi';

const SendTestEmailModal = ({
	isOpen,
	onClose,
	emailAccountId,
	campaignId,
	generatedEmailId,
	campaignMailId,
	template,
	initialRecipient,
	listIds,
}) => {
	const { user } = useAuth();
	const { showError, showSuccess } = useError();
	const [recipientEmail, setRecipientEmail] = useState(user?.email || '');
	const [isSending, setIsSending] = useState(false);
	const [sampleRecipient, setSampleRecipient] = useState(initialRecipient ?? null);
	const canPickRecipient = !generatedEmailId;
	// Without a campaign there is no sending account yet; the user picks one of the connected ones.
	const { data: accounts = [] } = useAllEmailAccounts();
	const connectedAccounts = accounts.filter((account) => account.connectionStatus === 'connected');
	const [pickedAccountId, setPickedAccountId] = useState('');
	const senderAccountId = emailAccountId || pickedAccountId || connectedAccounts[0]?.id || '';

	const handleSend = async (e) => {
		e.preventDefault();
		// React bubbles submit through portals: inside the template editor form this would save it too.
		e.stopPropagation();

		if (!recipientEmail.trim()) return;

		setIsSending(true);
		try {
			const body = {
				recipientEmail,
				emailAccountId: senderAccountId,
				...(campaignId && { campaignId }),
				...(generatedEmailId && { generatedEmailId }),
				...(campaignMailId && { campaignMailId }),
				...(template && { template }),
				...sampleRecipientRef(sampleRecipient),
			};

			await sendTestEmail(body);

			showSuccess({
				title: 'Test-E-Mail gesendet',
				message: `Die Test-E-Mail wurde an ${recipientEmail} gesendet.`,
				autoClose: true,
			});
			onClose();
		} catch (error) {
			showError({
				title: 'Fehler beim Senden',
				error,
			});
		} finally {
			setIsSending(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} as="form" onSubmit={handleSend}>
			<Panel.Header onClose={onClose}>Test-E-Mail senden</Panel.Header>
			<Panel.Body>
				<p className="text-sm text-bgBase-800 mb-4">
					Senden Sie eine Vorschau dieser E-Mail an eine beliebige Adresse. Tracking und Statistiken werden
					nicht beeinflusst.
				</p>
				{!emailAccountId && (
					<FormField label="Absender" htmlFor="testEmailSender" className="mb-4">
						<Select
							id="testEmailSender"
							value={senderAccountId}
							onChange={(e) => setPickedAccountId(e.target.value)}
							disabled={isSending}
						>
							{connectedAccounts.length === 0 && <option value="">Kein verbundenes E-Mail-Konto</option>}
							{connectedAccounts.map((account) => (
								<option key={account.id} value={account.id}>
									{account.emailAddress}
								</option>
							))}
						</Select>
					</FormField>
				)}
				{canPickRecipient && (
					<SampleRecipientField
						recipient={sampleRecipient}
						onChange={setSampleRecipient}
						listIds={listIds}
						disabled={isSending}
						className="mb-4"
					/>
				)}
				<FormField
					label="Senden an"
					htmlFor="testEmailRecipient"
					hint={template && 'Es wird Ihre Standard-Signatur angehängt.'}
				>
					<TextInput
						id="testEmailRecipient"
						type="email"
						value={recipientEmail}
						onChange={(e) => setRecipientEmail(e.target.value)}
						placeholder="test@example.com"
						required
						disabled={isSending}
					/>
				</FormField>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button type="button" variant="solid" onClick={onClose} disabled={isSending}>
					Abbrechen
				</Button>
				<Button
					type="submit"
					variant="solid"
					disabled={isSending || !recipientEmail.trim() || !senderAccountId}
				>
					{isSending ? (
						<div className="flex items-center gap-2">
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
							<span>Wird gesendet...</span>
						</div>
					) : (
						'Test senden'
					)}
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

SendTestEmailModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	// Campaign sender; without it the user picks one of the connected accounts.
	emailAccountId: PropTypes.string,
	campaignId: PropTypes.string,
	generatedEmailId: PropTypes.string,
	campaignMailId: PropTypes.string,
	// Unsaved editor text: { emailSubject, emailContent, emailTemplateId? }
	template: PropTypes.object,
	// Preselected placeholder recipient { name, crmContactId } or { name, crmCompanyId }, e.g. from the preview.
	initialRecipient: PropTypes.object,
	// Restricts the sample recipient to these contact lists (a campaign's); omitted = all lists.
	listIds: PropTypes.arrayOf(PropTypes.string),
};

export default SendTestEmailModal;
